import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Moon, Star, Download } from 'lucide-react';
import { usePricingPopup } from '../contexts/PricingPopupContext';
import { useCurrency } from '../contexts/CurrencyContext';
export function Hero() {
  const {
    openPopup
  } = usePricingPopup();
  const {
    formattedPrice
  } = useCurrency();
  return <section className="relative w-full bg-[#1e2a4a] text-[#faf6ef] pt-16 pb-24 px-6 overflow-hidden" data-id="element-67">
      {/* Background decorative elements */}
      <div className="absolute top-10 right-10 opacity-20 text-[#c9a96e]" data-id="element-68">
        <Moon size={64} data-id="element-69" />
      </div>
      <div className="absolute top-40 left-10 opacity-20 text-[#c9a96e]" data-id="element-70">
        <Star size={32} data-id="element-71" />
      </div>

      <div className="max-w-[680px] mx-auto relative z-10" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="flex flex-col items-center text-center mb-10" data-id="element-73">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#c9a96e]/30 text-[#c9a96e] text-sm font-medium tracking-wide mb-6 bg-[#c9a96e]/10 backdrop-blur-sm" data-id="element-74">
            <Star size={12} fill="currentColor" data-id="element-75" />
            Trusted by 2,000+ families
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-5 text-[#faf6ef]" data-id="element-76">
            End bedtime negotiations.
            <span className="block text-[#c9a96e] text-3xl md:text-4xl lg:text-5xl mt-2" data-id="element-77">
              Tonight.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-[#faf6ef]/80 max-w-xl leading-relaxed mb-8" data-id="element-78">
            A printable system for ages 4–6 that turns repeated get-ups into a
            non-event — so your evening doesn't disappear into a negotiation
            loop.
          </p>

          <button onClick={openPopup} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#1e2a4a] transition-all duration-200 bg-[#c9a96e] rounded-full hover:bg-[#d4b67d] hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c9a96e] focus:ring-offset-[#1e2a4a] shadow-lg shadow-[#c9a96e]/20" data-id="element-79">
            <Download className="mr-2 h-5 w-5" data-id="element-80" />
            Download & print tonight
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-81" />
          </button>
          <p className="mt-3 text-sm text-[#6b7fa3] font-medium" data-id="element-82">
            Instant PDF · {formattedPrice} · Start today
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="space-y-5 text-lg md:text-xl leading-[1.8] text-[#faf6ef]/90 font-light" data-id="element-83">
          <p data-id="element-84">It's never the first bedtime that gets you.</p>

          <div className="space-y-1" data-id="element-85">
            <p data-id="element-86">It's the second one.</p>
            <p data-id="element-87">And the third.</p>
            <p data-id="element-88">
              And the fifth "I just need…" that somehow appears the moment you
              finally sit down.
            </p>
          </div>

          <p className="font-medium text-[#c9a96e] italic" data-id="element-89">
            Water. Potty. One more hug. One more question. The blanket is
            "wrong."
            <br data-id="element-90" />
            "I'm scared."
            <br data-id="element-91" />
            "I'm not tired."
            <br data-id="element-92" />
            "Wait."
          </p>

          <p data-id="element-93">
            And because you're a good parent, you respond like a good parent:
          </p>
          <p data-id="element-94">You explain. You reassure. You negotiate. You problem-solve.</p>

          <div className="py-5 px-8 bg-[#faf6ef]/10 border-l-4 border-[#c9a96e] rounded-r-lg my-5" data-id="element-95">
            <p className="text-xl md:text-2xl font-serif font-bold text-[#faf6ef]" data-id="element-96">
              Bedtime turned into a conversation. Again.
            </p>
            <p className="text-base mt-2 text-[#faf6ef]/80" data-id="element-97">
              And your child learned there's still a way in.
            </p>
          </div>

          <p data-id="element-98">This isn't a character flaw.</p>
          <p className="font-medium text-[#faf6ef]" data-id="element-99">
            It's a structure problem.
          </p>

          <p className="text-2xl font-serif text-[#c9a96e] font-bold mt-3" data-id="element-100">
            So we fix the structure.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="mt-10 flex flex-col items-center" data-id="element-101">
          <button onClick={openPopup} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#1e2a4a] transition-all duration-200 bg-[#c9a96e] rounded-full hover:bg-[#d4b67d] hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c9a96e] focus:ring-offset-[#1e2a4a] shadow-lg shadow-[#c9a96e]/20" data-id="element-102">
            I want peaceful evenings
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-103" />
          </button>
          <p className="mt-3 text-sm text-[#6b7fa3] font-medium" data-id="element-104">
            Instant download · Print and start tonight
          </p>
        </motion.div>
      </div>
    </section>;
}